import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type {
  PostDefault,
  PostPlacement,
  PostPlacementInstance,
  Railing,
  RailingGeomTag,
  RailingGeomGroup,
  RailingPostPlacementType,
} from "building-types/settings/railingTypes";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { Table } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import type { Extrusion } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrudePolyLine2 } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { mat3Mult, mat3Rotate, mat3Translate } from "geom/mat3";
import { planeByThreePoints, planeNew, planeProject, planeProjectPolyLine3 } from "geom/plane";
import {
  polyLine2Copy,
  polyLine2EdgeStart,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2Transform,
  polyLine2VertexCount,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeTangent, polyLine3StartPoint, polyLine3Vertex, polyLine3VertexCount } from "geom/polyLine3";
import { rect2ByCenterWidthHeight } from "geom/rect2";
import type { IndexAABB2 } from "geom/rtree";
import { vec2AngleBetween, vec2Dist } from "geom/vec2";
import type RBush from "rbush";
import { uvGenerationForRailingPost } from "../../utils/uvGenerationForEntities";
import type { Path3OnSurfaces, Surface } from "./surfaces";
import { getMaxHeightOfSurfacesBelowPoint2 } from "./surfaces";

interface MakePostsBySettingsOptions {
  readonly postPlacementInstances: PostPlacementInstance[];
  readonly group: RailingGeomGroup;
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly topRailThicknessResolved: number;
  readonly post: PostDefault;
  readonly postHeight: number;
  readonly removeStartPost: boolean;
  readonly removeEndPost: boolean;
  readonly removeTransitionPosts: boolean | undefined;
  readonly materialSrc: MaterialSrc | undefined;
  readonly viewEntityDescriptor: ViewEntityDescriptor;
  readonly edgeIndexRemovePosts: { [index: number]: true };
  readonly buildingOutlineExtrusions: Extrusion[];
}

export function makePostsBySettings({
  railingPathEdge3s,
  group,
  viewEntityDescriptor,
  materialSrc,
  topRailThicknessResolved,
  post,
  postHeight,
  removeStartPost,
  removeEndPost,
  removeTransitionPosts = false,
  edgeIndexRemovePosts,
  postPlacementInstances,
  buildingOutlineExtrusions,
}: MakePostsBySettingsOptions) {
  const railingPathEdgeCount = railingPathEdge3s.length;
  for (let i = 0; i < railingPathEdgeCount; i++) {
    const { polyLine3, vertexAdditionalHeights } = railingPathEdge3s[i];
    if (!polyLine3) {
      continue;
    }

    const vertexCount = polyLine3VertexCount(polyLine3);
    const [startx, starty] = polyLine3StartPoint(polyLine3);
    for (let j = 0; j < vertexCount; j++) {
      const [nextx, nexty] = polyLine3Vertex(polyLine3, j);
      const dist2FromStart = vec2Dist(startx, starty, nextx, nexty);

      if (edgeIndexRemovePosts[i]) {
        continue;
      }

      const isStartPost = i === 0 && j === 0;
      if (removeStartPost) {
        if (isStartPost) {
          continue;
        }
      }
      const isEndPost = i === railingPathEdgeCount - 1 && j === vertexCount - 1;
      if (removeEndPost) {
        if (isEndPost) {
          continue;
        }
      }

      if (i !== railingPathEdgeCount - 1) {
        if (j === vertexCount - 1) {
          continue;
        }
      }

      if (removeTransitionPosts && !isStartPost && !isEndPost) {
        continue;
      }

      const [tx, ty] = polyLine3EdgeTangent(polyLine3, j < vertexCount - 1 ? j : j - 1);

      const [x, y, z] = polyLine3Vertex(polyLine3, j);

      const extrusion = extrudePostRectOnPlanVectors(
        x,
        y,
        z - vertexAdditionalHeights[j],
        postHeight + vertexAdditionalHeights[j] - topRailThicknessResolved / 2,
        post.depth,
        post.width,
        tx,
        ty,
      );

      if (isEndPost || isStartPost) {
        buildingOutlineExtrusions.push(extrusion);
      }

      group.group.add(extrusion, {
        type: "POST",
        placementType: railingPostPlacementTypeFromPosition(isStartPost, isEndPost),
        materialSrc,
        uvGen: uvGenerationForRailingPost(),
        viewEntityDescriptor,
      });

      postPlacementInstances.push({
        x,
        y,
        tx,
        ty,
        edgeIndex: i,
        param: dist2FromStart,
        removeInfillBefore: false,
        removeInfillAfter: false,
      });
    }
  }
}

interface MakePostsByPlacementsOptions {
  readonly postPlacementInstances: PostPlacementInstance[];
  readonly railingPathEdge2s: PolyLine2[];
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly railing: Railing;
  readonly level: Level;
  readonly levels: Table<Level>;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
  readonly groups: RailingGeomGroup[];
  readonly postHeight: number;
  readonly topRailThicknessResolved: number;
  readonly post: PostDefault;
  readonly postMaterial: MaterialSrc | undefined;
  readonly railingViewEntityDescriptor: ViewEntityDescriptor;
}

export function makePostsByPlacements({
  groups,
  railingViewEntityDescriptor,
  railing,
  railingPathEdge2s,
  railingPathEdge3s,
  postPlacementInstances,
  level,
  levels,
  levelStairSurfaceRTree,
  levelStairSurfaces,
  postHeight,
  topRailThicknessResolved,
  post,
  postMaterial,
  levelPerimeterSurfaceRTree,
  levelPerimeterSurfaces,
}: MakePostsByPlacementsOptions) {
  const postRect = rect2ByCenterWidthHeight(0, 0, post.width, post.depth);
  const vertexCount = polyLine2VertexCount(postRect);

  for (const { x: postx, y: posty, edgeIndex, id, param } of postPlacementInstances) {
    // project 2D post position onto 3D railing path by first projecting polyLine3 onto a vertical plane as a polyLine2
    const pl3 = railingPathEdge3s[edgeIndex].polyLine3;
    if (!pl3) {
      continue;
    }
    const railingPathEdge2 = railingPathEdge2s[edgeIndex];
    const [sx, sy] = polyLine2EdgeStart(railingPathEdge2, 0);
    const [tx, ty] = polyLine2EdgeTangent(railingPathEdge2, 0);
    const verticalRailingPlane = planeByThreePoints(sx, sy, 0, tx, ty, 0, sx, sy, 1);
    if (!verticalRailingPlane) {
      continue;
    }

    const polyLine3To2 = planeProjectPolyLine3(verticalRailingPlane, pl3);
    const [post2x, post2y] = planeProject(verticalRailingPlane, postx, posty, 0);

    const ints = intersectRay2PolyLine2(polyLine3To2, post2x, post2y, 0, 1);
    if (ints.length === 0) {
      continue;
    }
    // there should be only one intersection, but just in case, sort by t and take the first
    ray2PolyLine2IntersectionMakeUnique(ints);
    ints.sort((a, b) => a.t - b.t);

    // take intersection ray param t and use it to set the z value of the post against the sloping top/bottom rails
    const verticalOffsetFromIntersection = worldInchesNew(ints[0].t);
    const [x, y, z] = [postx, posty, verticalOffsetFromIntersection];

    const post2 = rotatePostRect2ToAlignWithVec2(tx, ty, x, y, postRect);

    const leastHeight = getLowestPolyLine2VertexHeightOnSurfaces({
      vertexCount,
      pl2: post2,
      level,
      levels,
      z,
      railing,
      levelStairSurfaceRTree,
      levelStairSurfaces,
      levelPerimeterSurfaceRTree,
      levelPerimeterSurfaces,
    });

    const postHandle: DocEntityHandle = { type: "RAILING_POST", id: id ?? "" };
    const group: RailingGeomGroup = {
      group: new GeomTagGroup<RailingGeomTag>(),
      pickHandle: postHandle,
    };

    const edgeLength = polyLine2EdgeLength(railingPathEdge2, 0);
    group.group.add(
      extrudePostRectOnPlanVectors(
        x,
        y,
        leastHeight,
        z + postHeight - leastHeight - topRailThicknessResolved / 2,
        post.depth,
        post.width,
        tx,
        ty,
      ),
      {
        type: "POST",
        placementType: railingPostPlacementTypeFromParam(
          param,
          edgeLength,
          edgeIndex === 0,
          edgeIndex === railingPathEdge2s.length - 1,
        ),
        materialSrc: postMaterial,
        uvGen: uvGenerationForRailingPost(),
        viewEntityDescriptor: railingViewEntityDescriptor,
      },
    );

    groups.push(group);
  }
}

function railingPostPlacementTypeFromPosition(isStartPost: boolean, isEndPost: boolean): RailingPostPlacementType {
  if (isStartPost) {
    return "START";
  }
  if (isEndPost) {
    return "END";
  }
  return "TRANSITION";
}

function railingPostPlacementTypeFromParam(
  param: number,
  edgeLength: number,
  isFirstPathEdge: boolean,
  isLastPathEdge: boolean,
): RailingPostPlacementType {
  if (isFirstPathEdge && Math.abs(param) < DIST_EPSILON) {
    return "START";
  }
  if (isLastPathEdge && Math.abs(edgeLength - param) < DIST_EPSILON) {
    return "END";
  }
  return "TRANSITION";
}

function extrudePostRectOnPlanVectors(
  x: number,
  y: number,
  z: number,
  height: number,
  depth: number,
  width: number,
  tx: number,
  ty: number,
): Extrusion {
  const len = Math.sqrt(tx * tx + ty * ty);
  let normalizedTx = 0;
  let normalizedTy = 1;
  if (len > DIST_EPSILON) {
    normalizedTx = tx / len;
    normalizedTy = ty / len;
  }
  const profile = rect2ByCenterWidthHeight(0, 0, depth, width);
  const plane = planeNew(x, y, z, normalizedTy, -normalizedTx, 0, normalizedTx, normalizedTy, 0);
  return extrudePolyLine2(profile, plane, 0, 0, height);
}

interface GetLowestPostCornerHeightOnSurfacesOptions {
  readonly pl2: PolyLine2;
  readonly vertexCount: number;
  readonly level: Level;
  readonly levels: Table<Level>;
  readonly z: WorldInches;
  readonly railing: Railing;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
}

function getLowestPolyLine2VertexHeightOnSurfaces({
  vertexCount,
  pl2,
  level,
  levels,
  z,
  railing,
  levelStairSurfaceRTree,
  levelStairSurfaces,
  levelPerimeterSurfaceRTree,
  levelPerimeterSurfaces,
}: GetLowestPostCornerHeightOnSurfacesOptions) {
  let leastHeight = Number.MAX_VALUE;
  for (let i = 0; i < vertexCount + 2; i += 2) {
    const [x, y] = [pl2[i], pl2[i + 1]];

    const height = getMaxHeightOfSurfacesBelowPoint2({
      level,
      levels,
      x,
      y,
      z,
      ignoreStairsAndRoomsAbove: railing.ignoreStairsAndRoomsAbove,
      levelStairSurfaceRTree,
      levelStairSurfaces,
      levelPerimeterSurfaceRTree,
      levelPerimeterSurfaces,
    });

    if (height < leastHeight) {
      leastHeight = height;
    }
  }
  return leastHeight;
}

/**
 * Post placements can remove posts from settings on the same edge.
 * @param edgeCount used to create correct number of entries if edgeIndex is undefined in post placement
 */
export function postRemovalTableNew(postPlacements: Table<PostPlacement>, edgeCount: number) {
  const edgeIndexRemovePosts: { [edgeIndex: number]: true } = {};
  for (const key in postPlacements) {
    const postPlacement = postPlacements[key];
    if (postPlacement.removePostsFromSettings) {
      if (postPlacement.edgeIndex === undefined) {
        for (let i = 0; i < edgeCount; i++) {
          edgeIndexRemovePosts[i] = true;
        }
      } else {
        edgeIndexRemovePosts[postPlacement.edgeIndex] = true;
      }
    }
  }
  return edgeIndexRemovePosts;
}

export function rotatePostRect2ToAlignWithVec2(tx: number, ty: number, x: number, y: number, postRect: PolyLine2) {
  const rotation = vec2AngleBetween(0, 1, tx, ty);
  let mat = mat3Rotate(rotation);
  mat = mat3Mult(mat3Translate(x, y), mat);
  const post2 = polyLine2Transform(polyLine2Copy(postRect), mat);
  return post2;
}
